/**
 * Pure srcset math — the client-side port of `ResponsiveImageBuilder::build()`.
 *
 * No DOM, no side effects: a {@link ResponsiveImageSpec} (+ optional source
 * dims/strategy/origin) in, a {@link ResponsiveImageView} out. This is the only
 * part that produces URLs, so it mirrors the PHP builder's semantics exactly
 * (doc 21 §5.1) — the same modes, the same `min(maxWidth, source)` src rule, the
 * same conservative fallback, the same degenerate-srcset guard:
 *
 *  - `remote` — dims always null (foreign host); w-ladder ∩ ≤ `maxWidth × 2`.
 *  - `fluid`  — w-ladder ∩ ≤ `min(source, maxWidth × 2)`; non-remote + unknown
 *               source → NO candidates (never guess a ladder, PHP `04 §3 [fix H2]`).
 *  - `fixed`  — x-descriptors `[W 1x, 2W 2x, 3W 3x]` capped at known source;
 *               lone-1x → no srcset; NO `sizes`.
 *  - passthrough strategy → NO candidates in any mode (degenerate-srcset guard).
 *
 * @see src/Services/ResponsiveImage/ResponsiveImageBuilder.php (the byte-for-byte semantics)
 * @see docs/speed-optimization/research/responsive-images/21-js-responsive-image-library-design.md §5.1, §9 step 4
 */
import { DPR, LADDER } from './config';
import { cloudflareStrategy } from './strategy';
import type {
  ImageSource,
  ResponsiveImageSpec,
  ResponsiveImageView,
  Strategy,
  TransformOptions,
} from './types';

/**
 * Build the responsive-image view-model for a spec.
 *
 * @param spec  the sizing directive (mode + cap + sizes + extraWidths + src)
 * @param opts  origin / strategy / source dims (all optional; sensible defaults)
 */
export function buildResponsiveImage(
  spec: ResponsiveImageSpec,
  opts: TransformOptions = {}
): ResponsiveImageView {
  const strategy: Strategy = opts.strategy ?? cloudflareStrategy(opts.origin ?? '');
  const src = spec.src ?? '';

  // `fixed` reads `width`; `fluid`/`remote` read `maxWidth`. Either is accepted
  // as the cap via `width ?? maxWidth`, clamped to ≥ 1 (PHP `max(1, width)`).
  const W = Math.max(1, spec.width ?? spec.maxWidth ?? 1);

  // Remote sources are never measured locally (foreign host) — PHP forces dims
  // null for remote even if a source is passed.
  const source: ImageSource | null = spec.mode === 'remote' ? null : (opts.source ?? null);

  // `src` always uses min(W, source) — never request more than exists.
  const srcWidth = source ? Math.min(W, source.w) : W;
  const srcUrl = strategy.transform(src, srcWidth);

  const candidates = buildCandidates(spec, strategy, W, source);

  // `sizes` only for the viewport-relative modes (fluid/remote), and only when
  // a srcset actually exists — without candidates it is a dangling (invalid)
  // attribute (mirrors the PHP builder). Fixed uses density descriptors.
  const sizes =
    spec.mode === 'fixed' || candidates.length === 0
      ? null
      : (spec.sizes ?? `(max-width:${W}px) 100vw, ${W}px`);

  // Intrinsic dimensions for CLS, only when actually known.
  let width: number | null = null;
  let height: number | null = null;
  if (source) {
    width = srcWidth;
    height = scaledHeight(srcWidth, source.w, source.h);
  }

  return {
    src: srcUrl,
    srcset: candidates.length > 0 ? candidates.join(', ') : null,
    sizes,
    width,
    height,
    mode: spec.mode,
  };
}

/**
 * The srcset candidate list (joined by the caller). Passthrough short-circuit:
 * when the strategy does not vary the URL by width, every candidate would carry
 * the identical URL — a degenerate srcset — so emit none in EVERY mode (PHP
 * `04 §3 H3`).
 */
function buildCandidates(
  spec: ResponsiveImageSpec,
  strategy: Strategy,
  maxWidth: number,
  source: ImageSource | null
): string[] {
  if (!strategy.producesDistinctWidths()) {
    return [];
  }

  return spec.mode === 'fixed'
    ? fixedCandidates(spec.src ?? '', strategy, maxWidth, source)
    : fluidCandidates(spec, strategy, maxWidth, source);
}

/**
 * `fixed` → x-descriptors `[W 1x, 2W 2x, 3W 3x]` (config DPR). Each W×dpr is
 * dropped past a KNOWN source (never upscale); when source is unknown the steps
 * are kept (a fixed width is explicit author intent). A lone 1x step is
 * redundant with `src` → no srcset.
 */
function fixedCandidates(
  src: string,
  strategy: Strategy,
  maxWidth: number,
  source: ImageSource | null
): string[] {
  const candidates: string[] = [];
  for (const dpr of DPR) {
    if (dpr < 1) {
      continue;
    }
    const width = maxWidth * dpr;
    // Drop a step that exceeds a KNOWN source (no upscaling past it).
    if (source && width > source.w) {
      continue;
    }
    candidates.push(`${strategy.transform(src, width)} ${dpr}x`);
  }

  // A lone 1x step is redundant with `src` → emit no srcset.
  return candidates.length <= 1 ? [] : candidates;
}

/**
 * `fluid` / `remote` → w-descriptors = (ladder ∪ extraWidths) ∩ ≤ cap, where
 * cap = `min(source, W×2)` (remote: source null → cap = W×2, the full ladder up
 * to retina). Non-remote with unknown dims → conservative fallback: no srcset.
 * `extraWidths` are ADDITIVE.
 */
function fluidCandidates(
  spec: ResponsiveImageSpec,
  strategy: Strategy,
  maxWidth: number,
  source: ImageSource | null
): string[] {
  // Non-remote with unknown dims → conservative fallback: no srcset.
  if (spec.mode !== 'remote' && !source) {
    return [];
  }

  const retinaCap = maxWidth * 2;
  const cap = source ? Math.min(source.w, retinaCap) : retinaCap;

  const widths: number[] = [];
  for (const w of LADDER) {
    if (w <= cap) {
      widths.push(w);
    }
  }
  for (const raw of spec.extraWidths ?? []) {
    const w = Math.trunc(raw);
    if (w > 0 && w <= cap) {
      widths.push(w);
    }
  }

  const unique = Array.from(new Set(widths)).sort((a, b) => a - b);

  return unique.map((w) => `${strategy.transform(spec.src ?? '', w)} ${w}w`);
}

/** Proportionally scaled intrinsic height for the chosen src width (CLS). */
function scaledHeight(srcWidth: number, sourceWidth: number, sourceHeight: number): number {
  if (sourceWidth <= 0) {
    return sourceHeight;
  }

  return Math.round(sourceHeight * (srcWidth / sourceWidth));
}
